// [claude-code 2026-05-03] Video cards now auto-play muted on expand, loop,
// stop on collapse (component unmount destroys the <video> element).
import { useEffect, useMemo, useState } from "react";
import { ExternalLink, MessageCircle, Video } from "lucide-react";
import type { RiskFlowAlert } from "../../lib/riskflow-feed";
import { BeatMissBadge } from "./BeatMissBadge";
import { isSourcePopupAllowed, openSourcePopup } from "../../lib/source-popup";
import { NothingFuse } from "../shared/NothingFuse";
import { AgenticFeedbackControls } from "../shared/AgenticFeedbackControls";
import {
  alertSeverityToPalette,
  fuseScoreFromAlert,
} from "../../lib/riskflow-card-utils";
import { colorForSeverity } from "../../lib/fuse-palette";
import { formatExpandedImpliedPointsLabel } from "../../lib/riskflow-expanded-card-utils";
import { RiskFlowExpandedFuseHeader } from "./RiskFlowExpandedFuseHeader";
import {
  riskFlowMentionedTickers,
  riskFlowVisualCandidates,
} from "../../lib/riskflow-visual-enrichment";
import { prefetchRiskFlowStockCharts } from "../../lib/riskflow-stock-chart-api";
import { RiskFlowEconHistoryFrame } from "./RiskFlowEconHistoryFrame";
import { RiskFlowEconReactionFrame } from "./RiskFlowEconReactionFrame";
import { RiskFlowEventFuseGrid } from "./RiskFlowEventFuseGrid";
import { RiskFlowVisualFrame } from "./RiskFlowVisualFrame";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

export type RiskFlowPostSurface = "full" | "timeline" | "mini";

interface RiskFlowPostCardProps {
  alert: RiskFlowAlert;
  surface?: RiskFlowPostSurface;
  active?: boolean;
  onAskAI?: (alert: RiskFlowAlert) => void;
  onNotRelevant?: (id: string, reason?: string) => void;
}

export function RiskFlowPostCard({
  alert,
  surface = "mini",
  active = true,
  onAskAI,
}: RiskFlowPostCardProps) {
  const fuseScore = fuseScoreFromAlert(alert);
  const severity = alertSeverityToPalette(alert.severity);
  const severityColor = colorForSeverity(severity);
  const shouldShowSourceActions = surface !== "full";
  const impliedPointsLabel = formatExpandedImpliedPointsLabel(alert);
  const canOpenSource = isSourcePopupAllowed(alert.url);
  const visualCandidates = active ? riskFlowVisualCandidates(alert) : [];
  const mentionedTickers = useMemo(
    () => riskFlowMentionedTickers(alert),
    [alert.headline, alert.symbols],
  );
  const [visualIndex, setVisualIndex] = useState(0);
  const activeVisual = visualCandidates[visualIndex] ?? null;
  const activeEconPrint =
    activeVisual?.kind === "econ-history" ? activeVisual.econPrint : null;
  const hasHighIvEconPrint = Boolean(activeEconPrint && fuseScore > 5.5);
  const hasMedia = Boolean(alert.videoUrl || activeVisual);
  const shouldShowInfoFallback = Boolean(
    active && !hasMedia && mentionedTickers.length === 0,
  );
  const hasVisualSlot = hasMedia || shouldShowInfoFallback;
  const [footerFuseCharged, setFooterFuseCharged] = useState(false);

  useEffect(() => {
    setFooterFuseCharged(false);
    const timer = window.setTimeout(() => setFooterFuseCharged(true), 540);
    return () => window.clearTimeout(timer);
  }, [alert.id, fuseScore]);

  useEffect(() => {
    prefetchRiskFlowStockCharts(mentionedTickers);
  }, [alert.headline, alert.id, alert.symbols, mentionedTickers]);

  useEffect(() => {
    setVisualIndex(0);
  }, [alert.headline, alert.id, alert.imageUrl]);

  return (
    <article className="relative bg-[rgba(5,4,2,0.42)] px-3 py-3 sm:px-4">
      <div className="min-w-0">
        {activeEconPrint ? (
          <div className="grid gap-3 sm:grid-cols-2 sm:items-stretch">
            {hasHighIvEconPrint ? (
              <RiskFlowEconReactionFrame
                analysis={activeEconPrint}
                surface={surface}
              />
            ) : (
              <div className="min-w-0 self-stretch">
                <RiskFlowEventFuseGrid
                  alert={alert}
                  severity={severity}
                  className="h-full self-stretch"
                />
              </div>
            )}
            <div className="relative block overflow-hidden rounded-[6px] border border-[var(--fintheon-accent)]/10 bg-[var(--fintheon-bg)]/50">
              <RiskFlowEconHistoryFrame
                analysis={activeEconPrint}
                surface={surface}
              />
            </div>
          </div>
        ) : (
          <div
            className={
              hasVisualSlot ? "grid gap-3 sm:grid-cols-2 sm:items-stretch" : ""
            }
          >
            {hasVisualSlot ? (
              <RiskFlowVisualFrame
                alert={alert}
                surface={surface}
                activeVisual={activeVisual}
                visualCandidates={visualCandidates}
                visualIndex={visualIndex}
                onVisualIndexChange={setVisualIndex}
                fallbackToInfo={shouldShowInfoFallback}
                onRequestBreakdown={
                  onAskAI
                    ? () => {
                        onAskAI(alert);
                      }
                    : undefined
                }
              />
            ) : null}
            <RiskFlowEventFuseGrid
              alert={alert}
              severity={severity}
              className={hasVisualSlot ? "self-stretch" : ""}
            />
          </div>
        )}

        {alert.agentNote && (
          <div className="mt-3 rounded-[4px] border border-[var(--fintheon-accent)]/8 bg-[var(--fintheon-bg)]/55 px-3 py-2">
            <span className="block text-[9px] font-semibold uppercase tracking-[0.14em] text-[var(--fintheon-text)]/35">
              Castra
            </span>
            <p className="mt-1 text-[11px] leading-relaxed text-[var(--fintheon-text)]/75">
              {alert.agentNote}
            </p>
          </div>
        )}

        {alert.econData?.beatMiss && (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <BeatMissBadge
              status={alert.econData.beatMiss}
              surprisePercent={alert.econData.surprisePercent}
            />
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] tabular-nums text-[var(--fintheon-text)]/55">
              {alert.econData.actual != null && (
                <span>
                  Actual{" "}
                  <b className="font-medium text-[var(--fintheon-text)]">
                    {alert.econData.actual}
                  </b>
                </span>
              )}
              {alert.econData.forecast != null && (
                <span>
                  Forecast{" "}
                  <b className="font-medium text-[var(--fintheon-text)]">
                    {alert.econData.forecast}
                  </b>
                </span>
              )}
              {alert.econData.previous != null && (
                <span>
                  Previous{" "}
                  <b className="font-medium text-[var(--fintheon-text)]">
                    {alert.econData.previous}
                  </b>
                </span>
              )}
            </div>
          </div>
        )}

        <footer className="mt-4">
          <div className="flex items-end gap-2">
            <div className="flex min-w-[88px] flex-1 items-end gap-2">
              <div className="min-w-0 flex-1">
                <RiskFlowExpandedFuseHeader
                  alert={alert}
                  impliedPointsLabel={impliedPointsLabel}
                />
                <div className="riskflow-expanded-horizontal-fuse">
                  <NothingFuse
                    value={
                      footerFuseCharged
                        ? Math.min(1, Math.max(0.1, fuseScore / 10))
                        : 0
                    }
                    severity={severity}
                    orientation="horizontal"
                    thickness={4}
                    segments={10}
                  />
                </div>
              </div>
              <span
                className="shrink-0 tabular-nums"
                style={{
                  fontFamily:
                    "'Doto', 'Readable Digits', var(--font-data, monospace)",
                  fontSize: 11,
                  fontWeight: 600,
                  color: severityColor,
                  lineHeight: 1,
                }}
              >
                <FintheonNumericText text={String(fuseScore.toFixed(1))} />
              </span>
            </div>
            {shouldShowSourceActions && alert.videoUrl && (
              <a
                href={alert.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-[10px] text-[var(--fintheon-text)]/35 transition-colors hover:text-[var(--fintheon-text)]/65"
              >
                <Video className="h-3 w-3" />
                Watch
              </a>
            )}
            {shouldShowSourceActions && canOpenSource && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openSourcePopup(alert.url);
                }}
                className="inline-flex items-center gap-1 text-[10px] text-[var(--fintheon-text)]/35 transition-colors hover:text-[var(--fintheon-text)]/65"
              >
                <ExternalLink className="h-3 w-3" />
                Source only
              </button>
            )}
            {onAskAI && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onAskAI(alert);
                }}
                className="fintheon-share-icon-button inline-flex min-h-8 min-w-8 items-center justify-center bg-transparent p-1 text-[var(--fintheon-text)]/55 transition-colors hover:text-[var(--fintheon-accent)] focus:outline-none focus:text-[var(--fintheon-accent)]"
                aria-label={`Share ${alert.headline} to chat`}
              >
                <MessageCircle className="h-[13px] w-[13px]" />
              </button>
            )}
          </div>
        </footer>
      </div>
      {shouldShowSourceActions && (
        <AgenticFeedbackControls surface="riskflow-item" itemId={alert.id} />
      )}
    </article>
  );
}
